import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { formatDateTableDisplay } from "@/lib/utils/date/date";
import { toCivilYmd } from "@/lib/utils/date/date-only";
import type { WidgetDataItemFromApi } from "./widgets-client";

export type ScheduledDropEnrollmentItem = {
  enrollmentId: number;
  studentId: number | null;
  studentName: string;
  classId: number | null;
  className: string;
  /** Civil date `YYYY-MM-DD` for API / feed enrichment. */
  dropDateYmd: string | null;
  dropDateLabel: string | null;
};

export function parseScheduledDropDateYmd(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) return toCivilYmd(raw);
  if (typeof raw === "string") return toCivilYmd(raw);
  return null;
}

function formatWidgetDropDate(raw: unknown): string | null {
  const ymd = parseScheduledDropDateYmd(raw);
  if (!ymd) return null;
  return formatDateTableDisplay(ymd);
}

export function mapScheduledDropEnrollmentRow(
  row: Record<string, unknown>,
): ScheduledDropEnrollmentItem {
  const enrollmentId = Number(row.enrollmentId);
  const studentIdRaw = row.studentId;
  const studentId =
    studentIdRaw != null && studentIdRaw !== "" ? Number(studentIdRaw) : null;

  return {
    enrollmentId: Number.isFinite(enrollmentId) ? enrollmentId : 0,
    studentId:
      studentId != null && Number.isFinite(studentId) ? studentId : null,
    studentName:
      typeof row.studentName === "string" && row.studentName.trim()
        ? row.studentName.trim()
        : "-",
    classId:
      row.classId != null &&
      row.classId !== "" &&
      Number.isFinite(Number(row.classId))
        ? Number(row.classId)
        : null,
    className:
      typeof row.className === "string" && row.className.trim()
        ? row.className.trim()
        : "Class",
    dropDateYmd: parseScheduledDropDateYmd(row.dropDate),
    dropDateLabel: formatWidgetDropDate(row.dropDate),
  };
}

/** Drop requests come from POST /api/widgets/data (`scheduled-drop-enrollments`), not enrollment GET. */
export function parseScheduledDropEnrollmentsFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): ScheduledDropEnrollmentItem[] {
  const row = dataItems.find(
    (item) => item.key === DashboardWidgetId.ScheduledDropEnrollments,
  );
  if (!row?.data) return [];

  const data = row.data as { $type?: string; items?: unknown[] };
  if (data.$type !== "scheduled-drop-enrollments") return [];
  if (!Array.isArray(data.items)) return [];

  return data.items
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === "object",
    )
    .map(mapScheduledDropEnrollmentRow)
    .filter((item) => item.enrollmentId > 0);
}
